import { z } from "zod";
import { OpaqueIdSchema } from "./schemas";
import { OpsDocBlocksSchema } from "./operations-docs-schemas";

export const CUSTOMER_PDF_MAX_BYTES = 10 * 1024 * 1024;
export const CustomerDocumentKindSchema = z.enum(["knowledge", "agreement", "quote"]);
export const DOCUMENT_STATUSES = {
  knowledge: ["current"],
  agreement: ["draft", "sent", "signed", "expired", "superseded"],
  quote: ["draft", "sent", "accepted", "declined", "expired", "withdrawn"],
} as const;
export const CustomerDocumentStatusSchema = z.enum([
  "current",
  "draft",
  "sent",
  "signed",
  "accepted",
  "declined",
  "expired",
  "superseded",
  "withdrawn",
]);
const optionalText = (maximum: number) => z.string().trim().min(1).max(maximum).nullable();
const HttpsUrlSchema = z
  .url()
  .max(2048)
  .refine((value) => {
    if (!URL.canParse(value)) return false;
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  }, "Use an HTTPS link without embedded credentials.");
export const CustomerDocumentSourceSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("page"),
    pageId: OpaqueIdSchema,
    revision: z.number().int().positive(),
  }),
  z.strictObject({ type: z.literal("file"), fileId: OpaqueIdSchema }),
  z.strictObject({ type: z.literal("link"), url: HttpsUrlSchema }),
]);
export const CustomerDocumentFieldsSchema = z.strictObject({
  kind: CustomerDocumentKindSchema,
  title: z.string().trim().min(1).max(200),
  summary: optionalText(2000),
  status: CustomerDocumentStatusSchema,
  agreementType: z.enum(["msa", "sow", "nda", "other"]).nullable(),
  reference: optionalText(80),
  amountMinor: z.number().int().min(0).max(999_999_999_999).nullable(),
  documentDate: z.iso.date().nullable(),
  expiresOn: z.iso.date().nullable(),
});
export const CustomerDocumentInputSchema = CustomerDocumentFieldsSchema.extend({
  source: CustomerDocumentSourceSchema,
})
  .refine((value) => (DOCUMENT_STATUSES[value.kind] as readonly string[]).includes(value.status), {
    message: "Choose a status for this document type.",
    path: ["status"],
  })
  .refine((value) => value.kind === "agreement" || value.agreementType === null, {
    message: "Agreement type only applies to agreements.",
    path: ["agreementType"],
  })
  .refine((value) => value.kind === "quote" || value.amountMinor === null, {
    message: "Only quotes have an amount.",
    path: ["amountMinor"],
  })
  .refine(
    (value) => !value.documentDate || !value.expiresOn || value.expiresOn >= value.documentDate,
    { message: "Expiry must be on or after the document date.", path: ["expiresOn"] },
  );
export const CustomerDocumentSnapshotSchema = z.strictObject({
  fields: CustomerDocumentInputSchema,
  blocks: OpsDocBlocksSchema.nullable(),
});
export const CustomerDocumentSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  draft: CustomerDocumentSnapshotSchema,
  published: CustomerDocumentSnapshotSchema.nullable(),
  revision: z.number().int().positive(),
  publishedRevision: z.number().int().positive().nullable(),
  publishedAt: z.number().int().positive().nullable(),
  updatedAt: z.number().int().positive(),
  archived: z.boolean(),
});
export const CustomerDocumentUpdateSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
  document: CustomerDocumentInputSchema,
});
export const CustomerDocumentActionSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
});
export const CustomerDocumentResponseSchema = z.strictObject({ document: CustomerDocumentSchema });
export const CustomerDocumentListSchema = z.strictObject({
  documents: z.array(CustomerDocumentSchema).max(50),
  nextCursor: OpaqueIdSchema.nullable(),
});
export const PublishedCustomerDocumentSchema = CustomerDocumentFieldsSchema.extend({
  id: OpaqueIdSchema,
  publishedAt: z.number().int().positive(),
  blocks: OpsDocBlocksSchema.nullable(),
  documentUrl: z.string().max(2048).nullable(),
  external: z.boolean(),
});
export const PublishedCustomerDocumentListSchema = z.strictObject({
  documents: z.array(PublishedCustomerDocumentSchema).max(50),
  nextCursor: OpaqueIdSchema.nullable(),
  administrator: z.boolean(),
});
export const CustomerDocumentQuerySchema = z.strictObject({
  agreementType: z.enum(["msa", "sow", "nda", "other"]).optional(),
  kind: CustomerDocumentKindSchema.optional(),
  search: z.string().trim().max(200).default(""),
  cursor: OpaqueIdSchema.optional(),
  archived: z.enum(["true", "false"]).default("false"),
});
export const CustomerDocumentFileSchema = z.strictObject({
  fileId: OpaqueIdSchema,
  filename: z.string().max(180),
  byteCount: z.number().int().positive(),
});
export type CustomerDocument = z.infer<typeof CustomerDocumentSchema>;
export type CustomerDocumentInput = z.infer<typeof CustomerDocumentInputSchema>;
export type CustomerDocumentKind = z.infer<typeof CustomerDocumentKindSchema>;
export type CustomerDocumentSnapshot = z.infer<typeof CustomerDocumentSnapshotSchema>;
export type CustomerDocumentQuery = z.infer<typeof CustomerDocumentQuerySchema>;
export type PublishedCustomerDocument = z.infer<typeof PublishedCustomerDocumentSchema>;

export function customerDocumentAudience(kind: CustomerDocumentKind): string {
  return kind === "knowledge" ? "All workspace members" : "Workspace administrators";
}
